import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { createAggregatedListing } from 'src/components/Listings/listingUtils';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { searchEvents, sortEvents } from 'src/utils/eventWithDataUtils';
import { getFlattenedListingIds } from 'src/utils/inventoryUtils';
import { createAggregatedPurchaseOrderTicketGroup } from 'src/utils/purchaseUtils';
import { createAggregatedSale } from 'src/utils/saleUtils';
import {
  ActionOutboxEntityType,
  type CatalogResults,
  type Event,
  type EventSort,
  type EventWithData,
  Feature,
  type Listing,
  type ListingGroup,
  type PurchaseOrderTicketGroup,
  type Sale,
} from 'src/WebApiController';

import type { ExpandedEventData } from './CatalogDataContext.types';
import { mergeListingDataIsLoaded } from './CatalogDataContext.utils';

export const useUpdateCatalogItemsInEvent = (
  queryClient: ReturnType<typeof useQueryClient>,
  queryKeyWithVersion: string,
  realExpandedDataItemsQueryKey: unknown[],
  transformEventData: (
    entityType:
      | ActionOutboxEntityType.Listing
      | ActionOutboxEntityType.Sale
      | ActionOutboxEntityType.TicketGroup
      | ActionOutboxEntityType.Purchase,
    data: EventWithData[],
    expandedEventData?: ExpandedEventData | null,
    isExpandedDataLoading?: boolean,
    filterZeroCounts?: boolean
  ) => EventWithData[],
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.TicketGroup
    | ActionOutboxEntityType.Purchase,
  searchText: string | null | undefined,
  performerIds: number[] | null | undefined,
  venueIds: number[] | null | undefined,
  isBoosted: boolean | null | undefined,
  boostedEventIds: Set<number> | null | undefined,
  sortBy: EventSort | null | undefined,
  isSortDescending: boolean | null,
  catalogResults: CatalogResults | undefined,
  setEventsTransformed: React.Dispatch<
    React.SetStateAction<EventWithData[] | undefined>
  >,
  queryWithExpandedItems: UseQueryResult<
    ExpandedEventData | null | undefined,
    Error
  >[]
) => {
  const hasAvoidRowVersionUpdateForSectionalUpdateFeature = useUserHasFeature(
    Feature.AvoidRowVersionUpdateForSectionalUpdate
  );
  // Store frequently-changing values in a ref so callbacks stay stable
  const latestRef = useRef({
    queryKeyWithVersion,
    realExpandedDataItemsQueryKey,
    transformEventData,
    entityType,
    searchText,
    performerIds,
    venueIds,
    isBoosted,
    boostedEventIds,
    sortBy,
    isSortDescending,
    hasAvoidRowVersionUpdateForSectionalUpdateFeature,
    catalogResults,
    queryWithExpandedItems,
  });
  latestRef.current = {
    queryKeyWithVersion,
    realExpandedDataItemsQueryKey,
    transformEventData,
    entityType,
    searchText,
    performerIds,
    venueIds,
    isBoosted,
    boostedEventIds,
    sortBy,
    isSortDescending,
    hasAvoidRowVersionUpdateForSectionalUpdateFeature,
    catalogResults,
    queryWithExpandedItems,
  };

  const searchAndTransform = useCallback(
    (newExpandedData: ExpandedEventData) => {
      const {
        transformEventData,
        entityType,
        searchText,
        performerIds,
        venueIds,
        isBoosted,
        boostedEventIds,
        sortBy,
        isSortDescending,
      } = latestRef.current;

      if (!latestRef.current.catalogResults) return;

      const transformed = sortEvents(
        latestRef.current.catalogResults,
        transformEventData(
          entityType,
          searchEvents(
            latestRef.current.catalogResults,
            searchText,
            performerIds,
            venueIds,
            null,
            isBoosted,
            boostedEventIds
          ),
          newExpandedData,
          false,
          true
        ),
        sortBy,
        isSortDescending
      );

      setEventsTransformed(transformed);
      return transformed;
    },
    [setEventsTransformed]
  );

  const updateExpandedListItems = useCallback(
    (updatedListingsByEvent: { [key: string]: Listing[] }) => {
      const { queryKeyWithVersion, realExpandedDataItemsQueryKey } =
        latestRef.current;

      const newDatas = queryClient.setQueriesData(
        {
          queryKey: [queryKeyWithVersion + 'Expanded'],
        },
        (oldData: ExpandedEventData | undefined) => {
          if (!oldData) {
            return oldData;
          }
          const newData = { ...oldData };
          Object.entries(updatedListingsByEvent).forEach(([key, value]) => {
            const oldListingsByIds =
              newData[key]?.listings?.reduce(
                (r, l) => {
                  r[l.id] = l;
                  return r;
                },
                {} as Record<number, Listing>
              ) ?? {};

            // Filter all the new ones that have row version < the old one
            const newListings = [
              ...value.filter((l) => {
                const oldListing = oldListingsByIds[l.id];
                if (oldListing && oldListing.rowVer > l.rowVer) {
                  return false;
                }

                return true;
              }),
            ];
            const newListingIdsFlattened = getFlattenedListingIds(newListings);

            // Retain listings that are not updated
            newData[key]?.listings?.forEach((originalListing) => {
              let shouldKeep = true;
              if (!originalListing.isLtGrp) {
                if (newListingIdsFlattened.includes(originalListing.id))
                  shouldKeep = false;
              } else {
                // If `originalListing` is a listing group
                // there will only be two cases
                // 1. No one listing in `newListingIdsFlattened` show up in the group (keep the group), or
                // 2. All listings in `newListingIdsFlattened` show up in the group (don't keep it, group has been edited or removed)
                const originalListingIdsFlattened = getFlattenedListingIds([
                  originalListing,
                ]);
                if (
                  originalListingIdsFlattened.every((l) =>
                    newListingIdsFlattened.includes(l)
                  )
                ) {
                  shouldKeep = false;
                }
              }

              if (shouldKeep) {
                newListings.push(originalListing);
              }
            });

            newData[key] = {
              sales: null,
              salesSummary: null,
              listings: newListings,
              listingsSummary:
                createAggregatedListing(
                  newListings.flatMap(flattenListingGroup).map((l) => ({
                    listing: l as Listing,
                    event: null as unknown as Event,
                  }))
                )?.listing ?? null,
              ticketGroups: null,
              ticketGroupsSummary: null,
              failedToRetrieveData: false,
            };
          });
          return newData;
        }
      );

      // We should only check at first 2 query keys [PrimaryKey, QueryObject]
      const newData = newDatas.find((qk) =>
        isEqual((qk[0] ?? []).slice(0, 2), realExpandedDataItemsQueryKey)
      );
      if (newData) {
        const transformed = searchAndTransform(newData[1] as ExpandedEventData);
        if (transformed) {
          console.debug(
            'eventsTransformed updated with updateExpandedListItems transformed: ' +
              transformed.length
          );
        }
      }
    },
    [queryClient, searchAndTransform]
  );

  const updateExpandedSaleItems = useCallback(
    (updatedListingsByEvent: { [key: string]: Sale[] }) => {
      const { queryKeyWithVersion, realExpandedDataItemsQueryKey } =
        latestRef.current;

      const newDatas = queryClient.setQueriesData(
        {
          queryKey: [queryKeyWithVersion + 'Expanded'],
        },
        (oldData: ExpandedEventData | undefined) => {
          if (!oldData) {
            return oldData;
          }
          const newData = { ...oldData };
          Object.entries(updatedListingsByEvent).forEach(([key, value]) => {
            const oldSalesByIds =
              newData[key]?.sales?.reduce(
                (r, s) => {
                  r[s.id] = s;
                  return r;
                },
                {} as Record<number, Sale>
              ) ?? {};

            // Filter all the new ones that have row version < the old one
            const newSales = [
              ...value.filter((s) => {
                const oldSale = oldSalesByIds[s.id];
                if (oldSale && oldSale.rowVer > s.rowVer) {
                  return false;
                }

                return true;
              }),
            ];
            const newSaleIds = newSales.map((s) => s.id);

            // Retain sales that are not in the newSales
            newData[key]?.sales?.forEach((originalSale) => {
              if (!newSaleIds.includes(originalSale.id)) {
                // new sales doesn't have the original sale, keep it
                newSales.push(originalSale);
              }
            });

            newData[key] = {
              sales: newSales,
              salesSummary: createAggregatedSale(newSales),
              listings: null,
              listingsSummary: null,
              ticketGroups: null,
              ticketGroupsSummary: null,
              failedToRetrieveData: false,
            };
          });
          return newData;
        }
      );

      const newData = newDatas.find((qk) =>
        isEqual((qk[0] ?? []).slice(0, 2), realExpandedDataItemsQueryKey)
      );
      if (newData) {
        const transformed = searchAndTransform(newData[1] as ExpandedEventData);
        if (transformed) {
          console.debug(
            'eventsTransformed updated with updateExpandedSaleItems transformed: ' +
              transformed.length
          );
        }
      }
    },
    [queryClient, searchAndTransform]
  );

  const updateExpandedTicketGroupItems = useCallback(
    (updatedTgsByEvent: { [key: string]: PurchaseOrderTicketGroup[] }) => {
      const { queryKeyWithVersion, realExpandedDataItemsQueryKey } =
        latestRef.current;

      const newDatas = queryClient.setQueriesData(
        {
          queryKey: [queryKeyWithVersion + 'Expanded'],
        },
        (oldData: ExpandedEventData | undefined) => {
          if (!oldData) {
            return oldData;
          }
          const newData = { ...oldData };
          Object.entries(updatedTgsByEvent).forEach(([key, value]) => {
            const oldTgsByIds =
              newData[key]?.ticketGroups?.reduce(
                (r, t) => {
                  r[t.tgId!] = t;
                  return r;
                },
                {} as Record<number, PurchaseOrderTicketGroup>
              ) ?? {};

            // Filter all the new ones that have row version < the old one
            const newTgs = [
              ...value.filter((t) => {
                const oldTg = oldTgsByIds[t.tgId!];
                if (oldTg && oldTg.rowVer > t.rowVer) {
                  return false;
                }

                return true;
              }),
            ];
            const newTgIds = newTgs.map((s) => s.tgId!);

            // Retain tgs that are not in the newTgs
            newData[key]?.ticketGroups?.forEach((originalTg) => {
              if (!newTgIds.includes(originalTg.tgId!)) {
                // new sales doesn't have the original sale, keep it
                newTgs.push(originalTg);
              }
            });

            newData[key] = {
              sales: null,
              salesSummary: null,
              listings: null,
              listingsSummary: null,
              ticketGroups: newTgs,
              ticketGroupsSummary:
                createAggregatedPurchaseOrderTicketGroup(newTgs),
              failedToRetrieveData: false,
            };
          });
          return newData;
        }
      );

      const newData = newDatas.find((qk) =>
        isEqual(qk[0], realExpandedDataItemsQueryKey)
      );
      if (newData) {
        const transformed = searchAndTransform(newData[1] as ExpandedEventData);
        if (transformed) {
          console.debug(
            'eventsTransformed updated with updateExpandedTicketGroupItems transformed: ' +
              transformed.length
          );
        }
      }
    },
    [queryClient, searchAndTransform]
  );

  const updateItemInEvent = useCallback(
    async (
      item: Listing | Sale | PurchaseOrderTicketGroup,
      entityType: ActionOutboxEntityType,
      sectionalUpdate = false // Flag to check if the update is from sectional API.
    ) => {
      const { hasAvoidRowVersionUpdateForSectionalUpdateFeature } =
        latestRef.current;

      // Now that we got the listing details, try to get the Event from existing data
      const catalogIdKey = item.viagVirtId!;
      const eventQuery = latestRef.current.queryWithExpandedItems.find(
        (r) => r.data?.[catalogIdKey]
      );

      // If we can't find it, go fetch it
      if (eventQuery?.data != null) {
        const event = eventQuery.data[catalogIdKey];

        // NOTE - after each refresh - the row version will be zero
        // and direct cache-update will trigger the row-version change until the next refresh
        // this is to make sure async updates to multiple listings/sales of an event do not overwrite each other
        if (entityType === ActionOutboxEntityType.Listing && event.listings) {
          // Because we're manually updating, set all rowVer here to be min 1, and the item to be updated to be min 2
          event.listings.forEach((l: Listing) => (l.rowVer = l.rowVer || 1));

          // If the event in the catalog has the listings expanded, we'll want to update it
          // with the latest info from the listing details above (this will help update state changes or seat allocations, etc.)
          const index = event.listings.findIndex(
            (s: Listing) =>
              s.id === item.id ||
              (s as ListingGroup).groupItems?.some(
                (l) =>
                  l.id === item.id ||
                  // this is the second level group
                  (l as ListingGroup).groupItems?.some(
                    (l2) => l2.id === item.id
                  )
              )
          );

          const oldItem = event.listings[index];
          const lg = oldItem as ListingGroup;

          if (oldItem.id === item.id) {
            const donotUpdateRowVer =
              hasAvoidRowVersionUpdateForSectionalUpdateFeature &&
              sectionalUpdate;
            if (!donotUpdateRowVer) {
              item.rowVer = oldItem.rowVer + 1; // Increase rowVer to indicate that we updated this recently
            }
            event.listings[index] = {
              ...oldItem,
              ...item,
              dataIsLoaded: mergeListingDataIsLoaded(oldItem, item as Listing),
            } as Listing;
          } else if (lg.groupItems != null) {
            // The item is a listing under a group
            const groupListingIndex = lg.groupItems.findIndex(
              (s) => s.id === item.id
            );

            if (groupListingIndex < 0) {
              for (const subGroup of lg.groupItems) {
                if (subGroup.isLtGrp) {
                  const listingIndex = (
                    subGroup as ListingGroup
                  ).groupItems.findIndex((s) => s.id === item.id);

                  if (listingIndex >= 0) {
                    const oldItemInGroup = (subGroup as ListingGroup)
                      .groupItems[listingIndex];
                    item.rowVer = oldItemInGroup.rowVer + 1; // Increase rowVer to indicate that we updated this recently

                    (subGroup as ListingGroup).groupItems[listingIndex] = {
                      ...oldItemInGroup,
                      ...item,
                      dataIsLoaded: mergeListingDataIsLoaded(
                        oldItemInGroup as Listing,
                        item as Listing
                      ),
                    } as Listing;
                  }
                }
              }
            } else {
              const oldItemInGroup = lg.groupItems[groupListingIndex];
              item.rowVer = oldItemInGroup.rowVer + 1; // Increase rowVer to indicate that we updated this recently

              lg.groupItems[groupListingIndex] = {
                ...oldItemInGroup,
                ...item,
                dataIsLoaded: mergeListingDataIsLoaded(
                  oldItemInGroup as Listing,
                  item as Listing
                ),
              } as Listing;
            }
          }

          event.listings = [...event.listings]; // this so the table will forced to be re-rendered
          updateExpandedListItems({ [catalogIdKey]: event.listings });
        } else if (entityType === ActionOutboxEntityType.Sale && event.sales) {
          // Because we're manually updating, set all rowVer here to be min 1, and the item to be updated to be min 2
          event.sales.forEach((s: Sale) => (s.rowVer = s.rowVer || 1));

          // If the event in the catalog has the sales expanded, we'll want to update it
          // with the latest info from the sale details above (this will help update state changes or seat allocations, etc.)
          const index = event.sales.findIndex((s: Sale) => s.id === item.id);
          const oldItem = event.sales[index];
          item.rowVer = oldItem.rowVer + 1; // Increase rowVer to indicate that we updated this recently

          event.sales[index] = { ...oldItem, ...item } as Sale;
          event.sales = [...event.sales]; // this so the table will forced to be re-rendered

          updateExpandedSaleItems({ [catalogIdKey]: event.sales });
        } else if (
          entityType === ActionOutboxEntityType.Purchase &&
          event.ticketGroups
        ) {
          // Because we're manually updating, set all rowVer here to be min 1, and the item to be updated to be min 2
          event.ticketGroups.forEach(
            (s: PurchaseOrderTicketGroup) => (s.rowVer = s.rowVer || 1)
          );

          // If the event in the catalog has the sales expanded, we'll want to update it
          // with the latest info from the sale details above (this will help update state changes or seat allocations, etc.)
          const index = event.ticketGroups.findIndex(
            (t: PurchaseOrderTicketGroup) =>
              t.tgId === (item as PurchaseOrderTicketGroup).tgId
          );
          const oldItem = event.ticketGroups[index];
          item.rowVer = oldItem.rowVer + 1; // Increase rowVer to indicate that we updated this recently

          event.ticketGroups[index] = {
            ...oldItem,
            ...item,
          } as PurchaseOrderTicketGroup;
          event.ticketGroups = [...event.ticketGroups]; // this so the table will forced to be re-rendered

          updateExpandedTicketGroupItems({
            [catalogIdKey]: event.ticketGroups,
          });
        }
      }
    },
    [
      updateExpandedListItems,
      updateExpandedSaleItems,
      updateExpandedTicketGroupItems,
    ]
  );

  return {
    updateExpandedListItems,
    updateExpandedSaleItems,
    updateExpandedTicketGroupItems,
    updateItemInEvent,
  };
};
